import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuthState } from "react-firebase-hooks/auth";
import ReactMarkdown from "react-markdown";
import {
  FileText,
  Copy,
  RefreshCw,
  Zap,
  AlignLeft,
  Type,
  CheckCircle,
  AlertCircle,
  Download,
  LayoutTemplate,
  Globe,
  PenTool
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import auth from "../../../firebase.init"; 

const BlogGenerator = () => {
  // ==========================================
  // 1. STATE MANAGEMENT
  // ==========================================
  const [user, loadingAuth] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  // User Data & Credits
  const [credits, setCredits] = useState(0);
  const [apiKeys, setApiKeys] = useState([]);

  // Form State
  const [topic, setTopic] = useState("");
  const [keywords, setKeywords] = useState("");
  const [tone, setTone] = useState("Informative & Educational");
  const [articleLength, setArticleLength] = useState("Medium (800-1200 words)");
  const [includeCta, setIncludeCta] = useState(true);

  // Output State
  const [generatedContent, setGeneratedContent] = useState(null);

  // Options
  const toneOptions = [
    "Informative & Educational",
    "Professional & Authoritative",
    "Conversational & Personal",
    "Persuasive & Sales-Oriented",
    "Witty & Entertaining"
  ];

  const lengthOptions = [
    "Short (500-800 words)",
    "Medium (800-1200 words)",
    "Long (1500+ words)"
  ];

  // ==========================================
  // 2. BACKEND / CREDIT LOGIC
  // ==========================================
  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`https://allinoneai-d1a5a1b54c64.herokuapp.com/api/gemini-keys`);
      if (response.data && response.data.success && response.data.data) {
        setApiKeys(response.data.data.map((keyObj) => keyObj.key));
      } else {
        setError("Failed to load API keys");
      }
    } catch (err) {
      console.error(err);
      setError("Error loading API keys");
    } finally {
      setLoadingKeys(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (loadingAuth) return;
      if (!user) {
        setLoading(false);
        setLoadingKeys(false);
        return;
      }
      try {
        await fetchApiKeys();
        const userResponse = await axios.get(`https://allinoneai-d1a5a1b54c64.herokuapp.com/users`, {
          params: { userEmail: user.email },
        });
        if (userResponse.data && userResponse.data.length > 0) {
          setCredits(userResponse.data[0].currentBalance);
        }
      } catch (err) {
        setError("Failed to load user data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, loadingAuth]);

  const getRandomApiKey = () => {
    if (apiKeys.length === 0) throw new Error("No API keys available");
    return apiKeys[Math.floor(Math.random() * apiKeys.length)];
  };

  // ==========================================
  // 3. AI GENERATION LOGIC
  // ==========================================

  const constructPrompt = () => {
    return `You are an expert SEO Content Writer. Write a complete, high-ranking blog article based on:
    
    Topic: ${topic}
    Primary Keywords: ${keywords}
    Tone: ${tone}
    Target Length: ${articleLength}
    Include CTA: ${includeCta ? "Yes" : "No"}

    **STRUCTURE REQUIREMENTS:**
    1. **SEO Meta Section**: Provide a Title Tag (60 chars) and Meta Description (160 chars).
    2. **H1 Headline**: Catchy and keyword-rich.
    3. **Introduction**: Hook the reader and state the problem/solution.
    4. **Body Content**: Use H2 and H3 subheadings. Break up text with short paragraphs. Use bolding for key phrases. Use bullet points where necessary.
    5. **Conclusion**: Summarize key takeaways.
    6. **Formatting**: Use Markdown strict syntax.

    **CRITICAL OUTPUT FORMAT:**
    Separate the Meta Data from the Article Body using a delimiter "---SPLIT---".
    
    Example Output Format:
    Title: [Title Here]
    Description: [Desc Here]
    ---SPLIT---
    # [Article H1 Title]
    [Rest of the article in Markdown]`;
  };

  const generateArticle = async () => {
    if (!topic.trim()) {
      toast.error("Please enter a topic.");
      return;
    }
    if (credits < 1) {
      setError("Insufficient credits.");
      return;
    }

    setGenerating(true);
    setError("");
    setGeneratedContent(null);

    try {
      // 1. Deduct Credit
      const deductResponse = await axios.post(
        `https://allinoneai-d1a5a1b54c64.herokuapp.com/api/deduct-credit`,
        { userEmail: user.email, amount: 1 }
      );

      if (!deductResponse.data.success) throw new Error("Failed to deduct credits");
      setCredits((prev) => prev - 1);

      // 2. Call AI
      const apiKey = getRandomApiKey();
      const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-lite:generateContent?key=${apiKey}`;
      
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [{ role: "user", parts: [{ text: constructPrompt() }] }],
          generationConfig: { temperature: 0.7, maxOutputTokens: 4000 },
        }),
      });

      if (!response.ok) throw new Error("AI Generation Failed");

      const data = await response.json();
      const textResult = data.candidates?.[0]?.content?.parts?.[0]?.text;

      if (textResult) {
        // Parse the Split
        const parts = textResult.split("---SPLIT---");
        let metaRaw = "";
        let bodyRaw = textResult;

        if (parts.length > 1) {
          metaRaw = parts[0].trim();
          bodyRaw = parts[1].trim();
        }

        setGeneratedContent({
          meta: metaRaw,
          body: bodyRaw,
          full: textResult.replace("---SPLIT---", "")
        });
        toast.success("Article generated successfully!");
      } else {
        throw new Error("Empty response from AI");
      }

    } catch (err) {
      console.error(err);
      toast.error(err.message);
      setError(err.message);

      // Refund Logic
      try {
        await axios.post(`https://allinoneai-d1a5a1b54c64.herokuapp.com/api/refund-credit`, {
          userEmail: user.email, amount: 1,
        });
        setCredits((prev) => prev + 1);
        toast("Credits refunded.");
      } catch (refundErr) { console.error(refundErr); }
    } finally {
      setGenerating(false);
    }
  };

  // ==========================================
  // 4. UTILS
  // ==========================================
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success("Copied to clipboard!"));
  };

  const downloadFile = (text, type = "md") => {
    const element = document.createElement("a");
    const file = new Blob([text], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = `article-${Date.now()}.${type}`;
    document.body.appendChild(element);
    element.click();
  };

  // ==========================================
  // 5. RENDER UI
  // ==========================================

  if (loadingAuth || loading || loadingKeys) {
    return (
      <div className="loader-wrapper">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="mt-3 text-muted fw-medium">Initializing Blog Engine...</p>
        
      </div>
    );
  }

  if (!user) {
    return (
      <div className="auth-wrapper">
        <div className="auth-card text-center">
          <div className="mb-4 icon-box mx-auto bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center" style={{width: 60, height: 60}}>
            <PenTool size={30} />
          </div>
          <h2 className="fw-bold text-dark mb-2">SEO Article Generator</h2>
          <p className="text-muted mb-4">Log in to create full-length blog content.</p>
          <button className="btn btn-primary btn-lg w-100 fw-semibold" onClick={() => window.location.href = "/login"}>
            Sign In
          </button>
        </div>
        
      </div>
    );
  }

  return (
    <div className="premium-app-container">
      
      {/* Navbar */}
      <nav className="premium-navbar">
        <div className="container-xl d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-3">
            <button className="btn-glass-icon" onClick={() => window.history.back()}>&larr;</button>
            <span className="premium-brand">
              <div className="brand-glow-icon"><FileText size={18} color="white" /></div>
              BlogGen AI
            </span>
          </div>
          <div className="credit-pill-glass">
            <Zap size={14} className="text-warning" fill="currentColor" />
            <span>{credits} Credits</span>
            {credits < 5 && <a href="/user/select-package" className="buy-btn-neon">Add</a>}
          </div>
        </div>
      </nav>

      <main className="container-xl py-4 py-lg-5">
        <div className="row g-4">
          
          {/* LEFT: Editor Configuration */}
          <div className="col-lg-4">
            <div className="glass-card h-100">
              <div className="glass-card-header">
                <LayoutTemplate size={18} className="text-info" />
                <h5 className="mb-0 fw-semibold text-white">Article Settings</h5>
              </div>

              <div className="p-4">
                {/* Topic */}
                <div className="mb-4 input-group-glass">
                  <label>Main Topic</label>
                  <textarea 
                    rows="3" 
                    placeholder="e.g. How to start a small business in 2024..."
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                  />
                </div>

                {/* Keywords */}
                <div className="mb-4 input-group-glass">
                  <label>Target Keywords</label>
                  <input 
                    type="text"
                    placeholder="e.g. entrepreneurship, funding, business plan"
                    value={keywords}
                    onChange={(e) => setKeywords(e.target.value)}
                  />
                </div>

                {/* Dropdowns */}
                <div className="mb-4 input-group-glass">
                  <label>Writing Tone</label>
                  <select value={tone} onChange={(e) => setTone(e.target.value)}>
                    {toneOptions.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>

                <div className="mb-4 input-group-glass">
                  <label>Approx. Length</label>
                  <select value={articleLength} onChange={(e) => setArticleLength(e.target.value)}>
                    {lengthOptions.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>

                {/* CTA Toggle */}
                <div className="mb-4 toggle-glass">
                  <label className="d-flex align-items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      className="glass-checkbox"
                      checked={includeCta}
                      onChange={(e) => setIncludeCta(e.target.checked)}
                    />
                    <span className="text-light">Include Call-to-Action (CTA)</span>
                  </label>
                </div>

                {/* Generate Button */}
                <button 
                  className="btn-neon-primary w-100"
                  onClick={generateArticle}
                  disabled={generating || credits < 1 || !topic.trim()}
                >
                  {generating ? (
                    <>
                      <RefreshCw className="spin me-2" size={18} /> Writing Article...
                    </>
                  ) : (
                    <>
                      <Zap size={18} className="me-2" fill="currentColor" /> Generate (1 Credit)
                    </>
                  )}
                </button>
                
                {error && (
                  <div className="mt-3 error-glass">
                    <AlertCircle size={14} className="me-2" /> {error}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT: Article Preview */}
          <div className="col-lg-8">
            <div className="glass-card h-100 d-flex flex-column">
              <div className="glass-card-header justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <AlignLeft size={18} className="text-info" />
                  <h5 className="mb-0 fw-semibold text-white">Article Preview</h5>
                </div>
                {generatedContent && (
                  <div className="d-flex gap-2">
                    <button className="btn-glass-sm" onClick={() => copyToClipboard(generatedContent.full)} title="Copy All">
                      <Copy size={16} /> <span className="d-none d-md-inline ms-1">Copy</span>
                    </button>
                    <button className="btn-glass-sm" onClick={() => downloadFile(generatedContent.full)} title="Download Markdown">
                      <Download size={16} />
                    </button>
                  </div>
                )}
              </div>

              <div className="glass-card-body flex-grow-1 position-relative">
                {generating ? (
                  <div className="state-container">
                    <div className="neon-spinner mb-4"></div>
                    <h4 className="text-white fw-bold">Drafting your masterpiece...</h4>
                    <p className="text-light opacity-75">Structuring headings, optimizing SEO, and polishing content.</p>
                  </div>
                ) : generatedContent ? (
                  <div className="output-scroll p-4 p-md-5">
                    {/* SEO Meta Box */}
                    {generatedContent.meta && (
                      <div className="meta-box-glass mb-5">
                        <div className="meta-box-header">
                          <Globe size={14} /> SEO Meta Data
                        </div>
                        <div className="meta-content-styled">
                          {generatedContent.meta}
                        </div>
                      </div>
                    )}
                    {/* Main Article Content */}
                    <div className="article-prose-glass">
                      <ReactMarkdown>{generatedContent.body}</ReactMarkdown>
                    </div>
                  </div>
                ) : (
                  <div className="state-container opacity-50">
                    <Type size={64} className="text-white opacity-25 mb-3" />
                    <h4 className="text-white mt-3">Ready to Write</h4>
                    <p className="text-light opacity-75">Configure your topic and settings to generate a full article.</p>
                  </div>
                )}
              </div>

              {generatedContent && (
                <div className="glass-card-footer">
                  <span className="text-success d-flex align-items-center gap-1 small fw-bold">
                    <CheckCircle size={14} /> Content Generated
                  </span>
                  <span className="text-light opacity-75 small">
                    ~{generatedContent.full.split(' ').length} words
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      
    </div>
  );
};

export default BlogGenerator;